// Подсветка найденного — та же логика, что у bit-history/app/search.py:
// нижний регистр и ё→е, слово ищется по основе (без двух последних букв,
// не короче четырёх) с начала слова, фраза в кавычках — целиком.
//
// Стоп-слова — тот же список, что STOP_WORDS в search.py: Mongo их выбрасывает,
// и запись по ним не находится, а подсветка «что» в каждой строке расшифровки
// только мешала бы найти то, что искали.
const STOP = new Set((
  'и в во не что он на я с со как а то все она так его но да ты к у же вы за бы по '
  + 'только ее мне было вот от меня еще нет о из ему теперь когда даже ну вдруг ли '
  + 'если уже или ни быть был него до вас нибудь опять уж вам ведь там потом себя '
  + 'ничего ей может они тут где есть надо ней для мы тебя их чем была сам чтоб без '
  + 'будто чего раз тоже себе под будет ж тогда кто этот того потому этого какой '
  + 'совсем ним здесь этом один почти мой тем чтобы нее сейчас были куда зачем всех '
  + 'никогда можно при наконец два об другой хоть после над больше тот через эти нас '
  + 'про всего них какая много разве три эту моя впрочем хорошо свою этой перед иногда '
  + 'лучше чуть том нельзя такой им более всегда конечно всю между'
).split(' '));

// Длина строки не меняется — смещения годятся для исходного текста. Редкие
// буквы вроде «İ» в нижнем регистре дают два символа, их оставляем как есть.
const norm = (s) => {
  let low = s.toLowerCase();
  if (low.length !== s.length) {
    low = Array.from(s, (c) => (c.toLowerCase().length === c.length ? c.toLowerCase() : c)).join('');
  }
  return low.replace(/ё/g, 'е');
};
const WORD = /[\p{L}\p{N}_]+/gu;
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Слова и фразы запроса: как `search.parse_query`, непарная кавычка — просто символ. */
export function queryTerms(q) {
  const phrases = [];
  const rest = (q || '').replace(/"([^"]*)"/g, (_, p) => {
    const ph = (norm(p).match(WORD) || []).join(' ');
    if (ph) phrases.push(ph);
    return ' ';
  });
  const words = [];
  for (const w of norm(rest).match(WORD) || []) {
    if (w.length >= 2 && !STOP.has(w) && !words.includes(w)) words.push(w);
  }
  return { words, phrases };
}

const stem = (w) => (w.length <= 4 ? w : w.slice(0, Math.max(4, w.length - 2)));

/**
 * Режет текст на куски «найдено / не найдено» по смещениям `[начало, конец)`.
 * Смещения за краем текста и наложения отбрасываются: сервер считает их в
 * символах Python, и у эмодзи они могут разойтись со строкой JS.
 */
export function splitByHighlights(text, highlights) {
  const out = [];
  let pos = 0;
  for (const [s, e] of [...(highlights || [])].sort((a, b) => a[0] - b[0])) {
    if (s < pos || e > text.length || s >= e) continue;
    if (s > pos) out.push({ text: text.slice(pos, s), hit: false });
    out.push({ text: text.slice(s, e), hit: true });
    pos = e;
  }
  if (pos < text.length || out.length === 0) out.push({ text: text.slice(pos), hit: false });
  return out;
}

// Больше подсветок не рисуем: мегабайтная расшифровка с частым словом дала бы
// тысячи <mark>. Сервер режет так же (`find_spans`, `limit`).
export const MAX_SPANS = 500;

const WORD_CHAR = /[\p{L}\p{N}_]/u;

// Вхождения `re` с начала слова (перед ними нет буквы или цифры), не больше
// MAX_SPANS. Предыдущий символ проверяется вручную, а не `(?<!…)`: lookbehind
// в регулярке iOS Safari до 16.4 не понимает и падает SyntaxError ещё при
// сборке RegExp. На отказе поиск идёт дальше со следующего символа — как у
// lookbehind, так фраза внутри более длинного совпадения не теряется.
function wordStartMatches(low, re) {
  const out = [];
  let m;
  while (out.length < MAX_SPANS && (m = re.exec(low)) !== null) {
    const prev = Array.from(low.slice(Math.max(0, m.index - 2), m.index)).pop();
    if (prev && WORD_CHAR.test(prev)) re.lastIndex = m.index + 1;
    else out.push([m.index, m.index + m[0].length]);
  }
  return out;
}

function spansFor(text, { words, phrases }) {
  if (!text || (!words.length && !phrases.length)) return [];
  const low = norm(text);
  const spans = [];
  const collect = (re) => spans.push(...wordStartMatches(low, re));
  for (const p of phrases) collect(new RegExp(escapeRe(p) + '(?![\\p{L}\\p{N}_])', 'gu'));
  for (const w of words) collect(new RegExp(escapeRe(stem(w)) + '[\\p{L}\\p{N}_]*', 'gu'));
  spans.sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const s of spans) {
    const last = merged[merged.length - 1];
    if (last && s[0] < last[1]) last[1] = Math.max(last[1], s[1]);
    else merged.push([...s]);
  }
  return merged.slice(0, MAX_SPANS);
}

/** Вхождения запроса в текст — слитые смещения, как `find_spans` на сервере. */
export const highlightSpans = (text, q) => spansFor(text, queryTerms(q));

export const highlightQuery = (text, q) => splitByHighlights(text || '', highlightSpans(text, q));

/**
 * Подсветка для документа из многих кусков (реплики расшифровки, текстовые
 * узлы протокола): запрос разбирается один раз, а предел MAX_SPANS общий на
 * все куски по порядку вызова — иначе он держал бы только каждую реплику
 * по отдельности. Возвращает функцию `text -> Array<{text, hit}>`.
 */
export function createHighlighter(q, max = MAX_SPANS) {
  const terms = queryTerms(q);
  let left = max;
  return (text) => {
    const src = text || '';
    if (left <= 0) return [{ text: src, hit: false }];
    const spans = spansFor(src, terms).slice(0, left);
    left -= spans.length;
    return splitByHighlights(src, spans);
  };
}
